import i18next from "i18next";
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { type LanguageKey, languages } from "../i18n";

// Define the context type
interface LanguageContextType {
	currentLanguage: LanguageKey;
	changeLanguage: (lang: LanguageKey) => void;
	languages: typeof languages;
}

// Create the context with a default value
const LanguageContext = createContext<LanguageContextType>({
	currentLanguage: "en_US",
	changeLanguage: () => {},
	languages,
});

// Storage key for persisting language preference
const LANGUAGE_STORAGE_KEY = "pilatestime-admin-language";

// Provider component
export function LanguageProvider({ children }: { children: React.ReactNode }) {
	// Initialize with stored language or default to en_US
	const [currentLanguage, setCurrentLanguage] = useState<LanguageKey>(() => {
		const storedLanguage = localStorage.getItem(
			LANGUAGE_STORAGE_KEY,
		) as LanguageKey | null;
		return storedLanguage && languages[storedLanguage]
			? storedLanguage
			: "en_US";
	});

	// Change language function - memoized with useCallback
	const changeLanguage = useCallback((lang: LanguageKey) => {
		if (languages[lang]) {
			setCurrentLanguage(lang);
			i18next.changeLanguage(lang);
			localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);

			// Update the html lang attribute
			document.documentElement.lang = lang.split("_")[0];
		}
	}, []);

	// Initialize language on mount
	useEffect(() => {
		changeLanguage(currentLanguage);
	}, [currentLanguage, changeLanguage]);

	const value = {
		currentLanguage,
		changeLanguage,
		languages,
	};

	return (
		<LanguageContext.Provider value={value}>
			{children}
		</LanguageContext.Provider>
	);
}

// Custom hook to use the language context
export function useLanguage() {
	const context = useContext(LanguageContext);
	if (context === undefined) {
		throw new Error("useLanguage must be used within a LanguageProvider");
	}
	return context;
}
